import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronLeft, ChevronRight, Star, ArrowRight } from 'lucide-react';

import { cn } from "../../lib/utils";
import { Badge } from "./badge";
import { Button } from "./button";

export const PlaceCard = ({
  images,
  tags,
  rating,
  title,
  dateRange,
  hostType,
  isTopRated = false,
  description,
  pricePerNight, 
  className,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  // Carousel image change handler
  const changeImage = (newDirection) => {
    setDirection(newDirection);
    setCurrentIndex((prevIndex) => {
      const nextIndex = prevIndex + newDirection;
      if (nextIndex < 0) return images.length - 1;
      if (nextIndex >= images.length) return 0;
      return nextIndex;
    });
  };

  // Animation variants for the carousel
  const carouselVariants = {
    enter: (direction) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
    }),
  };

  // Animation variants for staggering content
  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5 }}
      variants={contentVariants}
      whileHover={{ 
        scale: 1.02, 
        boxShadow: '0px 10px 30px -5px rgba(0,0,0,0.5)',
        transition: { type: 'spring', stiffness: 300, damping: 20 }
      }}
      className={cn(
        'w-full overflow-hidden rounded-3xl border border-white/10 liquid-glass text-white shadow-lg cursor-pointer flex flex-col',
        className
      )}
    >
      {/* Image Carousel Section */}
      <div className="relative group h-56 md:h-64 shrink-0 overflow-hidden bg-black/50">
        <AnimatePresence initial={false} custom={direction}>
          <motion.img
            key={currentIndex}
            src={images[currentIndex]}
            alt={title}
            custom={direction}
            variants={carouselVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 },
            }}
            className="absolute h-full w-full object-cover mix-blend-screen opacity-80 group-hover:opacity-100 transition-opacity duration-300"
          />
        </AnimatePresence>
        
        {/* Carousel Navigation */}
        <div className="absolute inset-0 flex items-center justify-between p-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <Button variant="ghost" size="icon" className="rounded-full bg-black/50 hover:bg-black/80 text-white z-10 h-8 w-8 ml-2 backdrop-blur-md border border-white/10" onClick={(e) => { e.preventDefault(); changeImage(-1); }}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="rounded-full bg-black/50 hover:bg-black/80 text-white z-10 h-8 w-8 mr-2 backdrop-blur-md border border-white/10" onClick={(e) => { e.preventDefault(); changeImage(1); }}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Top Badges and Rating */}
        <div className="absolute top-3 left-3 flex gap-2 z-10">
          {tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="bg-black/50 text-white backdrop-blur-md border border-white/10 font-body font-normal text-xs px-2 py-0.5 rounded-full">
              {tag}
            </Badge>
          ))}
        </div>
        <div className="absolute top-3 right-3 z-10">
          <Badge variant="secondary" className="flex items-center gap-1 bg-black/50 text-white backdrop-blur-md border border-white/10 font-body font-normal text-xs px-2 py-0.5 rounded-full">
            <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" /> {rating}
          </Badge>
        </div>

        {/* Pagination Dots */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={(e) => { e.preventDefault(); setCurrentIndex(index); }}
              className={cn(
                'h-1 w-1 rounded-full transition-all duration-300',
                currentIndex === index ? 'w-3 bg-white' : 'bg-white/40 hover:bg-white/70'
              )}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Content Section */}
      <motion.div variants={contentVariants} className="p-6 flex flex-col flex-grow space-y-4">
        <motion.div variants={itemVariants} className="flex justify-between items-start gap-4">
          <h3 className="text-xl md:text-2xl font-heading text-white">{title}</h3>
          {isTopRated && <Badge variant="outline" className="shrink-0 text-white/80 border-white/20 whitespace-nowrap bg-white/5 font-body font-normal text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full">Featured</Badge>}
        </motion.div>

        <motion.div variants={itemVariants} className="text-xs font-body text-white/40 flex gap-2 items-center">
          <span>{dateRange}</span> &bull; <span>{hostType}</span>
        </motion.div>

        <motion.p variants={itemVariants} className="text-sm font-body text-white/60 leading-relaxed line-clamp-2 flex-grow">
          {description}
        </motion.p>

        <motion.div variants={itemVariants} className="flex justify-between items-center pt-4 border-t border-white/10 mt-auto">
          <p className="font-medium text-white/90 text-sm">
            {pricePerNight} <span className="text-xs font-normal text-white/40">read</span>
          </p>
          <Button variant="ghost" className="group text-white hover:bg-white/10 hover:text-white rounded-full px-4 h-9 font-body text-sm">
            Read Post
            <ArrowRight className="h-3.5 w-3.5 ml-1.5 transition-transform group-hover:translate-x-1" />
          </Button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};
